import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { ConfluencePagesInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-confluence-pages-inline-edit-full/src/ui/confluence-pages/index.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import type { ConfluencePagesCell$key } from './__generated__/ConfluencePagesCell.graphql';

export type Props = {
	fieldRef: ConfluencePagesCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const ConfluencePagesCell = ({ fieldRef, isReadOnlyWithoutHoverPopover }: Props) => {
	const data = useFragment<ConfluencePagesCell$key>(
		graphql`
			fragment ConfluencePagesCell on JiraConfluenceRemoteIssueLinksField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...confluencePages_issueFieldConfluencePagesInlineEditFull_ConfluencePagesInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);

	const shouldTruncate = useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

	return (
		<ConfluencePagesInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={(isEditable && !isReadOnlyWithoutHoverPopover) ?? false}
			isTruncated={shouldTruncate}
			menuPosition="fixed"
			readViewFitContainerHeight
		/>
	);
};
